// Shared formatting utilities for field values
// This module contains reusable formatting functions that can be used across components

// ========================================
// STANDARD FORMAT OPTIONS FOR CONFIGURATION MANAGERS
// ========================================

// Text formatting options
export const TEXT_FORMAT_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: 'italic', label: 'Italic' },
  { value: 'underline', label: 'Underline' },
  { value: 'bold-italic', label: 'Bold + Italic' },
  { value: 'bold-underline', label: 'Bold + Underline' },
  { value: 'italic-underline', label: 'Italic + Underline' },
  { value: 'bold-italic-underline', label: 'Bold + Italic + Underline' },
  { value: 'uppercase', label: 'UPPERCASE' },
  { value: 'lowercase', label: 'lowercase' },
  { value: 'capitalize', label: 'Capitalize' }
];

// Date formatting options
export const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (15/06/2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (06/15/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-06-15)' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (15-06-2024)' },
  { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY (15-Jun-2024)' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY (15 Jun 2024)' },
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Jun 15, 2024)' },
  { value: 'MMMM DD, YYYY', label: 'MMMM DD, YYYY (June 15, 2024)' },
  { value: 'DD MMMM YYYY', label: 'DD MMMM YYYY (15 June 2024)' }
];

// Time formatting options
export const TIME_FORMAT_OPTIONS = [
  { value: 'HH:mm', label: 'HH:mm (24-hour, no seconds)' },
  { value: 'HH:mm:ss', label: 'HH:mm:ss (24-hour with seconds)' },
  { value: 'hh:mm A', label: 'hh:mm AM/PM (12-hour, no seconds)' },
  { value: 'hh:mm:ss A', label: 'hh:mm:ss AM/PM (12-hour with seconds)' },
  { value: 'DD/MM/YYYY HH:mm', label: 'DD/MM/YYYY HH:mm (date + time)' },
  { value: 'DD/MM/YYYY HH:mm:ss', label: 'DD/MM/YYYY HH:mm:ss (date + time + seconds)' }
];

// Number formatting options
export const NUMBER_FORMAT_OPTIONS = [
  { value: '0', label: '0 (whole numbers)' },
  { value: '0.0', label: '0.0 (1 decimal)' },
  { value: '0.00', label: '0.00 (2 decimals)' },
  { value: '0.000', label: '0.000 (3 decimals)' },
  { value: '0.0000', label: '0.0000 (4 decimals)' },
  { value: '0,000', label: '0,000 (thousands separator)' },
  { value: '0,000.0', label: '0,000.0 (thousands + 1 decimal)' },
  { value: '0,000.00', label: '0,000.00 (thousands + 2 decimals)' }
];

// Currency formatting options
export const CURRENCY_FORMAT_OPTIONS = [
  { value: '$0', label: '$0 (dollars, no cents)' },
  { value: '$0.0', label: '$0.0 (dollars, 1 decimal)' },
  { value: '$0.00', label: '$0.00 (dollars and cents)' },
  { value: '$0,000', label: '$0,000 (thousands)' },
  { value: '$0,000.0', label: '$0,000.0 (thousands + 1 decimal)' },
  { value: '$0,000.00', label: '$0,000.00 (thousands with cents)' },
  { value: '0 USD', label: '0 USD (suffix)' },
  { value: '0.00 USD', label: '0.00 USD (suffix with decimals)' }
];

// Coordinate formatting options (Latitude/Longitude)
export const COORDINATE_FORMAT_OPTIONS = [
  { value: 'decimal', label: 'Decimal Degrees (12.34567°)' },
  { value: 'dms', label: 'Degrees Minutes Seconds (12° 20\' 44.412")' },
  { value: 'dm', label: 'Degrees Minutes (12° 20.740\')' },
  { value: 'dms-hemisphere', label: 'DMS with Hemisphere (12° 20\' 44.412" N)' },
  { value: 'dm-hemisphere', label: 'DM with Hemisphere (12° 20.740\' N)' },
  { value: 'decimal-hemisphere', label: 'Decimal with Hemisphere (12.34567° N)' }
];

// Field width options for configuration
export const FIELD_WIDTH_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'full', label: 'Full Width' },
  { value: '50px', label: '50px' },
  { value: '100px', label: '100px' },
  { value: '120px', label: '120px' },
  { value: '150px', label: '150px' },
  { value: '200px', label: '200px' },
  { value: '300px', label: '300px' }
];

// Field format types
export const FIELD_FORMAT_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'coordinate', label: 'Coordinate (Lat/Lng)' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'lookup', label: 'Lookup Field (Database Reference)' }
];

// Column types for database mapping
export const COLUMN_TYPES = [
  { value: 'string', label: 'String/Text' },
  { value: 'textarea', label: 'Long Text/Textarea' },
  { value: 'int', label: 'Integer' },
  { value: 'float', label: 'Float/Decimal' },
  { value: 'decimal2', label: 'Decimal (2 places)' },
  { value: 'decimal4', label: 'Decimal (4 places)' },
  { value: 'comma', label: 'Number with Commas' },
  { value: 'currency', label: 'Currency' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'time', label: 'Time' },
  { value: 'boolean', label: 'Boolean (True/False)' },
  { value: 'selectable', label: 'Selectable/Dropdown' }
];

// Field input options (determines input behavior, separate from formatting)
export const FIELD_INPUT_OPTIONS = [
  { value: undefined, label: 'Standard Input' },
  { value: 'selectable', label: 'Dropdown/Select' },
  { value: 'lookup', label: 'Lookup Field (Database Reference)' }
];

// Default format values
export const DEFAULT_FORMATS = {
  text: { textFormat: 'normal' },
  textarea: { textFormat: 'normal' },
  date: { dateFormat: 'DD/MM/YYYY' },
  time: { timeFormat: 'HH:mm' },
  number: { numberFormat: '0.00' },
  currency: { currencyFormat: '$0.00' },
  coordinate: { coordinateFormat: 'dms' },
  boolean: { textFormat: 'normal' }
};

// ========================================
// FORMATTING FUNCTIONS
// ========================================

// Helper function to resolve global units
const resolveUnit = (field, globalSettings = null) => {
    if (!field?.unit || field.unit.trim() === '') {
        return '';
    }
    
    // If unit is "global", resolve it using global settings
    if (field.unit.toLowerCase() === 'global' && globalSettings) {
        // Use a simplified detection logic to avoid circular dependencies
        const fieldName = (field.name || '').toLowerCase();
        const fieldLabel = (field.label || '').toLowerCase();
        const searchText = `${fieldName} ${fieldLabel}`;
        
        // Simple category detection
        if (searchText.includes('depth') || searchText.includes('length') || searchText.includes('distance') || 
            searchText.includes('elevation') || searchText.includes('height') || searchText.includes('td') || 
            searchText.includes('measured') || searchText.includes('vertical')) {
            return globalSettings.depth || field.unit;
        }
        if (searchText.includes('temp') || searchText.includes('temperature') || searchText.includes('heat')) {
            return globalSettings.temperature || field.unit;
        }
        if (searchText.includes('pressure') || searchText.includes('press') || searchText.includes('psi') || 
            searchText.includes('bar') || searchText.includes('pascal')) {
            return globalSettings.pressure || field.unit;
        }
        if (searchText.includes('weight') || searchText.includes('mass') || searchText.includes('load') || 
            searchText.includes('force')) {
            return globalSettings.weight || field.unit;
        }
        if (searchText.includes('volume') || searchText.includes('flow') || searchText.includes('rate') || 
            searchText.includes('capacity')) {
            return globalSettings.volume || field.unit;
        }
        
        // Default fallback to depth for global unit
        return globalSettings.depth || field.unit;
    }
    
    return field.unit;
};

// Enhanced field value formatting function that handles both DDR format and column config format
export const formatFieldValue = (value, field, globalSettings = null) => {
    if (!value && value !== 0) return '-';
    
    // Determine which format configuration to use
    // Priority: field.format (from config_column.json) > field type (legacy)
    let formatType = 'text';
    let formatOptions = {};
    
    if (field.format) {
        // Use new format system from config_column.json
        formatType = field.format;
        formatOptions = {
            dateFormat: field.dateFormat,
            timeFormat: field.timeFormat,
            numberFormat: field.numberFormat,
            currencyFormat: field.currencyFormat,
            coordinateFormat: field.coordinateFormat,
            textFormat: field.textFormat
        };
    } else if (field.type) {
        // Fallback to legacy type-based formatting
        formatType = mapColumnTypeToFormatType(field.type);
        formatOptions = getDefaultFormatSettings(formatType);
    }
    
    // Handle different field formats
    let formattedValue;
    switch (formatType) {
        case 'date':
            formattedValue = formatDateValue(value, formatOptions.dateFormat);
            break;
        case 'time':
            formattedValue = formatTimeValue(value, formatOptions.timeFormat);
            break;
        case 'number':
            formattedValue = formatNumberValue(value, formatOptions.numberFormat);
            break;
        case 'currency':
            formattedValue = formatCurrencyValue(value, formatOptions.currencyFormat);
            break;
        case 'coordinate':
            // Determine coordinate type from field name/id
            const coordinateType = (field.name || field.id || '').toLowerCase().includes('lat') ? 'lat' : 'lng';
            formattedValue = formatCoordinateValue(value, formatOptions.coordinateFormat, coordinateType);
            break;
        case 'textarea':
            formattedValue = applyTextFormatting(value, formatOptions.textFormat);
            break;
        default:
            formattedValue = applyTextFormatting(value, formatOptions.textFormat);
    }
    
    // Add resolved unit if available (only for non-currency types as currency includes symbol)
    if (formatType !== 'currency') {
        const resolvedUnit = resolveUnit(field, globalSettings);
        if (resolvedUnit && resolvedUnit.trim() !== '' && resolvedUnit.toLowerCase() !== 'global') {
            formattedValue += ` ${resolvedUnit}`;
        }
    }
    
    return formattedValue;
};

// Function to format field value using column configuration format
export const formatFieldValueWithColumnConfig = (value, columnConfig, globalSettings = null) => {
    if (!value && value !== 0) return '-';
    
    // Use the enhanced formatFieldValue with column config
    return formatFieldValue(value, columnConfig, globalSettings);
};

// Legacy function for backward compatibility
export const formatFieldValueLegacy = (value, field, globalSettings = null) => {
    if (!value && value !== 0) return '-';
    
    // Handle different field formats (legacy approach)
    let formattedValue;
    switch (field.format) {
        case 'date':
            formattedValue = formatDateValue(value, field.dateFormat);
            break;
        case 'time':
            formattedValue = formatTimeValue(value, field.timeFormat);
            break;
        case 'number':
            formattedValue = formatNumberValue(value, field.numberFormat);
            break;
        case 'currency':
            formattedValue = formatCurrencyValue(value, field.currencyFormat);
            break;
        case 'coordinate':
            // Determine coordinate type from field name/id
            const coordinateType = (field.name || field.id || '').toLowerCase().includes('lat') ? 'lat' : 'lng';
            formattedValue = formatCoordinateValue(value, field.coordinateFormat, coordinateType);
            break;
        case 'textarea':
            formattedValue = applyTextFormatting(value, field.textFormat);
            break;
        default:
            formattedValue = applyTextFormatting(value, field.textFormat);
    }
    
    // Add resolved unit if available
    const resolvedUnit = resolveUnit(field, globalSettings);
    if (resolvedUnit && resolvedUnit.trim() !== '' && resolvedUnit.toLowerCase() !== 'global') {
        formattedValue += ` ${resolvedUnit}`;
    }
    
    return formattedValue;
};

// Date formatting function
export const formatDateValue = (value, dateFormat = 'DD/MM/YYYY') => {
    if (!value) return '-';
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthNamesLong = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        
        switch (dateFormat) {
            case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
            case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
            case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
            case 'DD-MM-YYYY': return `${day}-${month}-${year}`;
            case 'DD-MMM-YYYY': return `${day}-${monthNames[date.getMonth()]}-${year}`;
            case 'DD MMM YYYY': return `${day} ${monthNames[date.getMonth()]} ${year}`;
            case 'MMM DD, YYYY': return `${monthNames[date.getMonth()]} ${day}, ${year}`;
            case 'MMMM DD, YYYY': return `${monthNamesLong[date.getMonth()]} ${day}, ${year}`;
            case 'DD MMMM YYYY': return `${day} ${monthNamesLong[date.getMonth()]} ${year}`;
            default: 
                // Handle generic patterns by replacing with proper month names
                let result = dateFormat
                    .replace('MMMM', monthNamesLong[date.getMonth()])
                    .replace('MMM', monthNames[date.getMonth()])
                    .replace('MM', month)
                    .replace('DD', day)
                    .replace('YYYY', year);
                return result;
        }
    } catch (error) {
        return value;
    }
};

// Time formatting function
export const formatTimeValue = (value, timeFormat = 'HH:mm') => {
    if (!value) return '-';
    
    console.log('🕐 formatTimeValue - Input:', value, 'Format:', timeFormat);
    
    try {
        // Handle simple time strings directly without Date conversion to avoid timezone issues
        if (typeof value === 'string') {
            // If it's already in HH:MM or H:MM format, parse directly
            if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
                const timeParts = value.split(':');
                const hours24 = parseInt(timeParts[0]);
                const minutes = parseInt(timeParts[1]);
                const seconds = parseInt(timeParts[2] || 0);
                const hours12 = hours24 % 12 || 12;
                const ampm = hours24 >= 12 ? 'PM' : 'AM';
                
                const pad = (num) => num.toString().padStart(2, '0');
                
                let result;
                switch (timeFormat) {
                    case 'HH:mm':
                        result = `${pad(hours24)}:${pad(minutes)}`;
                        break;
                    case 'HH:mm:ss':
                        result = `${pad(hours24)}:${pad(minutes)}:${pad(seconds)}`;
                        break;
                    case 'hh:mm A':
                        result = `${pad(hours12)}:${pad(minutes)} ${ampm}`;
                        break;
                    case 'hh:mm:ss A':
                        result = `${pad(hours12)}:${pad(minutes)}:${pad(seconds)} ${ampm}`;
                        break;
                    default:
                        result = `${pad(hours24)}:${pad(minutes)}`;
                }
                
                console.log('🕐 formatTimeValue - Direct time parsing result:', result);
                return result;
            }
            
            // If it's a datetime string, extract time part without Date conversion
            if (value.includes('T')) {
                const timePart = value.split('T')[1];
                if (timePart) {
                    const timeOnly = timePart.substring(0, 8); // Get HH:MM:SS or HH:MM
                    console.log('🕐 formatTimeValue - Extracted time from datetime:', timeOnly);
                    return formatTimeValue(timeOnly, timeFormat); // Recursive call with extracted time
                }
            }
        }
        
        // Fallback to Date object for other formats (but this might cause timezone issues)
        let date;
        if (typeof value === 'string') {
            date = new Date(value);
        } else {
            date = new Date(value);
        }
        
        if (isNaN(date.getTime())) {
            console.log('🕐 formatTimeValue - Invalid date, returning original:', value);
            return value;
        }
        
        const hours24 = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const hours12 = hours24 % 12 || 12;
        const ampm = hours24 >= 12 ? 'PM' : 'AM';
        
        const pad = (num) => num.toString().padStart(2, '0');
        
        let result;
        switch (timeFormat) {
            case 'HH:mm':
                result = `${pad(hours24)}:${pad(minutes)}`;
                break;
            case 'HH:mm:ss':
                result = `${pad(hours24)}:${pad(minutes)}:${pad(seconds)}`;
                break;
            case 'hh:mm A':
                result = `${pad(hours12)}:${pad(minutes)} ${ampm}`;
                break;
            case 'hh:mm:ss A':
                result = `${pad(hours12)}:${pad(minutes)}:${pad(seconds)} ${ampm}`;
                break;
            default:
                result = `${pad(hours24)}:${pad(minutes)}`;
        }
        
        console.log('🕐 formatTimeValue - Date object result:', result);
        return result;
    } catch (err) {
        console.log('🕐 formatTimeValue - Error:', err);
        return value;
    }
};

// Number formatting function
export const formatNumberValue = (value, numberFormat = '0.00') => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    switch (numberFormat) {
        case '0': return Math.round(num).toString();
        case '0.0': return num.toFixed(1);
        case '0.00': return num.toFixed(2);
        case '0.000': return num.toFixed(3);
        case '0.0000': return num.toFixed(4);
        case '0,000': return Math.round(num).toLocaleString('en-US');
        case '0,000.0': return num.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        case '0,000.00': return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        default: return num.toString();
    }
};

// Currency formatting function
export const formatCurrencyValue = (value, currencyFormat = '$0.00') => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    switch (currencyFormat) {
        case '$0': return `$${Math.round(num).toLocaleString('en-US')}`;
        case '$0.0': return `$${num.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        case '$0.00': return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        case '$0,000': 
            if (num % 1 === 0) {
                return `$${Math.round(num).toLocaleString('en-US')}`;
            } else {
                return `$${num.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
            }
        case '$0,000.0': return `$${num.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        case '$0,000.00': return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        case '0 USD': return `${Math.round(num).toLocaleString('en-US')} USD`;
        case '0.00 USD': return `${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} USD`;
        default: return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
};

// Coordinate formatting function (latitude/longitude)
export const formatCoordinateValue = (value, coordinateFormat = 'dms', coordinateType = 'lat') => {
    if (!value && value !== 0) return '-';
    
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    // Determine hemisphere
    let hemisphere = '';
    if (coordinateFormat.includes('hemisphere')) {
        if (coordinateType.toLowerCase().includes('lat')) {
            hemisphere = num >= 0 ? ' N' : ' S';
        } else {
            hemisphere = num >= 0 ? ' E' : ' W';
        }
    }
    
    const absValue = Math.abs(num);
    
    switch (coordinateFormat) {
        case 'decimal':
        case 'decimal-hemisphere':
            return `${absValue.toFixed(5)}°${hemisphere}`;
            
        case 'dm':
        case 'dm-hemisphere':
            const degrees1 = Math.floor(absValue);
            const minutes1 = (absValue - degrees1) * 60;
            return `${degrees1}° ${minutes1.toFixed(3)}'${hemisphere}`;
            
        case 'dms':
        case 'dms-hemisphere':
        default:
            const degrees = Math.floor(absValue);
            const minutesFloat = (absValue - degrees) * 60;
            const minutes = Math.floor(minutesFloat);
            const seconds = (minutesFloat - minutes) * 60;
            return `${degrees}° ${minutes}' ${seconds.toFixed(3)}"${hemisphere}`;
    }
};

// Text formatting function
export const applyTextFormatting = (value, textFormat = 'normal') => {
    if (!value) return '-';
    
    let text = String(value);
    
    // Apply case transformations first
    switch (textFormat) {
        case 'uppercase':
            text = text.toUpperCase();
            break;
        case 'lowercase':
            text = text.toLowerCase();
            break;
        case 'capitalize':
            text = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
            break;
    }
    
    // Apply styling formatting (HTML tags for display)
    switch (textFormat) {
        case 'bold':
            return `<strong>${text}</strong>`;
        case 'italic':
            return `<em>${text}</em>`;
        case 'underline':
            return `<u>${text}</u>`;
        case 'bold-italic':
            return `<strong><em>${text}</em></strong>`;
        case 'bold-underline':
            return `<strong><u>${text}</u></strong>`;
        case 'italic-underline':
            return `<em><u>${text}</u></em>`;
        case 'bold-italic-underline':
            return `<strong><em><u>${text}</u></em></strong>`;
        default:
            return text;
    }
};

// Utility function for formatting numbers (used in multiple places)
export const formatNumber = (num) => {
    if (!num) return '-';
    return new Intl.NumberFormat('en-US').format(num);
};

// Helper function to check if two dates are the same (moved from DailyDrillingReport)
export const isSameDate = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();
};

// Helper function to find adjacent report (moved from DailyDrillingReport)
export const findAdjacentReport = (reports, currentDate, direction) => {
    const currentTime = new Date(currentDate).getTime();
    const sortedReports = reports
        .map(r => new Date(r.ReportDate).getTime())
        .filter(t => direction === 'prev' ? t < currentTime : t > currentTime)
        .sort((a, b) => direction === 'prev' ? b - a : a - b);

    return sortedReports.length > 0 ? sortedReports[0] : null;
};

// ========================================
// UTILITY FUNCTIONS FOR FORMAT MANAGEMENT
// ========================================

// Get default format settings for a field type
export const getDefaultFormatSettings = (fieldType) => {
    return DEFAULT_FORMATS[fieldType] || DEFAULT_FORMATS.text;
};

// Map legacy column types to standard format types
export const mapColumnTypeToFormatType = (columnType) => {
    const typeMap = {
        'string': 'text',
        'textarea': 'textarea',
        'int': 'number',
        'float': 'number',
        'decimal2': 'number',
        'decimal4': 'number',
        'comma': 'number',
        'currency': 'currency',
        'date': 'date',
        'datetime': 'time',
        'time': 'time',
        'boolean': 'boolean',
        'selectable': 'text'
    };
    return typeMap[columnType] || 'text';
};

// Map database types to column types
export const mapDbTypeToColumnType = (dbType) => {
    if (!dbType) return 'string';
    const type = String(dbType).toLowerCase();
    
    if (type.includes('date') && !type.includes('time')) return 'date';
    if (type.includes('datetime') || type.includes('timestamp')) return 'datetime';
    if (type.includes('money') || type.includes('currency')) return 'currency';
    if (type.includes('int')) return 'int';
    if (type.includes('decimal') || type.includes('numeric')) return 'decimal4';
    if (type.includes('float') || type.includes('real')) return 'float';
    if (type.includes('bit') || type.includes('bool')) return 'boolean';
    if (type.includes('text') || type.includes('ntext')) return 'textarea';
    
    return 'string'; // Default fallback
};

// Validate and normalize format values
export const normalizeFormatValue = (formatType, formatValue) => {
    const validOptions = {
        text: TEXT_FORMAT_OPTIONS.map(opt => opt.value),
        textarea: TEXT_FORMAT_OPTIONS.map(opt => opt.value),
        date: DATE_FORMAT_OPTIONS.map(opt => opt.value),
        time: TIME_FORMAT_OPTIONS.map(opt => opt.value),
        number: NUMBER_FORMAT_OPTIONS.map(opt => opt.value),
        currency: CURRENCY_FORMAT_OPTIONS.map(opt => opt.value),
        coordinate: COORDINATE_FORMAT_OPTIONS.map(opt => opt.value)
    };
    
    const validValues = validOptions[formatType];
    if (!validValues) return null;
    
    // If the format value is valid, return it
    if (validValues.includes(formatValue)) {
        return formatValue;
    }
    
    // Try to migrate common legacy formats
    const legacyMigrations = {
        'decimal2': '0.00',
        'decimal4': '0.0000',
        'comma': '0,000',
        'percentage': '0.00',
        // Add more migrations as needed
    };
    
    if (legacyMigrations[formatValue]) {
        return legacyMigrations[formatValue];
    }
    
    // Return default if no valid format found
    const defaults = {
        text: 'normal',
        textarea: 'normal',
        date: 'DD/MM/YYYY',
        time: 'HH:mm',
        number: '0.00',
        currency: '$0.00',
        coordinate: 'dms'
    };
    
    return defaults[formatType] || 'normal';
};

// Check if a field is configured as selectable (dropdown)
export const isFieldSelectable = (field) => {
  // Check both the legacy 'type' field and the new 'options' field
  return field?.type === 'selectable' || 
         field?.options === 'selectable' ||
         field?.type === 'textselectable';
};

// Check if a field is configured as a lookup field
export const isFieldLookup = (field) => {
  // Check if the field has lookup configuration
  // Support both format-based (DDR Layout Manager) and options-based (Column Configuration) lookup detection
  const hasLookupFormat = field?.format === 'lookup';
  const hasLookupOptions = field?.options === 'lookup';
  const hasLookupConfig = field?.lookupTable && field?.lookupValueColumn && field?.lookupDisplayColumn;
  
  const isLookup = (hasLookupFormat || hasLookupOptions) && hasLookupConfig;
  
  if (field && (field.format === 'lookup' || field.options === 'lookup' || field.lookupTable)) {
    console.log('🔍 isFieldLookup check:', {
      fieldName: field.name || field.dbColumn,
      format: field.format,
      options: field.options,
      lookupTable: field.lookupTable,
      lookupValueColumn: field.lookupValueColumn,
      lookupDisplayColumn: field.lookupDisplayColumn,
      hasLookupFormat,
      hasLookupOptions,
      hasLookupConfig,
      isLookup,
      fullField: field
    });
  }
  
  return isLookup;
};

// Get lookup configuration for a field
export const getLookupConfig = (field) => {
  if (!isFieldLookup(field)) {
    return null;
  }
  
  return {
    table: field.lookupTable,
    valueColumn: field.lookupValueColumn,
    displayColumn: field.lookupDisplayColumn,
    dbSchema: field.lookupDbSchema || 'dbo'
  };
};

// Generate field configuration with proper formatting
export const generateFieldConfig = (columnInfo, options = {}) => {
    const {
        useColumnConfigFormat = true,
        defaultGroup = 1,
        defaultWidth = 'auto'
    } = options;
    
    // Determine field format type
    let formatType = 'text';
    if (columnInfo.format && useColumnConfigFormat) {
        formatType = mapColumnTypeToFormatType(columnInfo.format);
    } else if (columnInfo.DATA_TYPE) {
        const columnType = mapDbTypeToColumnType(columnInfo.DATA_TYPE);
        formatType = mapColumnTypeToFormatType(columnType);
    }
    
    // Generate field name and label
    const fieldId = (columnInfo.COLUMN_NAME || columnInfo.name || '').toLowerCase();
    let fieldName = columnInfo.label || columnInfo.COLUMN_NAME || columnInfo.name || '';
    
    // Format field name
    if (!columnInfo.label) {
        fieldName = fieldName.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
        fieldName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    }
    
    // Add unit to field name if available
    if (columnInfo.unit && columnInfo.unit.trim()) {
        fieldName += ` (${columnInfo.unit})`;
    }
    
    // Get default formatting for the field type
    const defaultFormatting = getDefaultFormatSettings(formatType);
    
    return {
        id: fieldId,
        name: fieldName,
        enabled: true,
        format: formatType,
        width: defaultWidth,
        dbColumn: columnInfo.COLUMN_NAME || columnInfo.name,
        group: defaultGroup,
        ...defaultFormatting
    };
}; 